import type { VersantBenefits } from '../types'
import SmsRecoverySection from './SmsRecoverySection'
import VisionSlideoutFrame, { type VisionCheckSummary } from './VisionSlideoutFrame'
import CheckIdentityEditor from './CheckIdentityEditor'
import { UNSAVED_WARNING } from '../utils/unsavedWork'
import { useUnsavedGuard } from '../utils/useUnsavedGuard'
import { canCorrect } from '../utils/checkCorrection'
import type { EligibilityCheckDoc } from '../types'

/** A field Versant printed empty. */
export const VERSANT_NOT_SHOWN = 'Not shown by Versant.'
/** A block the robot could not read. */
export const VERSANT_COULD_NOT_READ = 'Could not be read. Try the check again.'
export const VERSANT_DETAILED_FAILED = 'Detailed benefits could not be read. Try the check again.'
export const VERSANT_NOT_FOUND = 'No patient in Versant with this name and date of birth.'

interface VersantSlideoutProps {
  benefits: VersantBenefits
  summary: VisionCheckSummary
  onClose: () => void
  inRecoveryLoop?: boolean
  smsRecoveryEligible?: boolean
  onViewRecoveryConversation?: () => void
  orgId?: string
  check?: EligibilityCheckDoc | null
  onCorrected?: () => void
  onCorrectionFired?: () => void
  canWrite?: boolean
  onDirtyChange?: (dirty: boolean) => void
  warnedByParent?: boolean
}

const printed = (value: string | null): string => value ?? VERSANT_NOT_SHOWN

/**
 * The Versant panel (openspec change versant-eligibility-lane,
 * frontend/versant-slideout): for a found patient, top to bottom, the picked
 * row's patient and plan fields as Versant printed them (member ID, Group/Sub
 * Group and Plan Name/Plan Prefix in the header; the name and date of birth
 * as Versant spells them and the Line of Business under it), the Benefit
 * Messages as a numbered list, the benefits table as printed, and the
 * detailed benefits' text. A member-not-found check shows its sentence and no
 * benefits.
 */
export default function VersantSlideout({
  benefits, summary, onClose, inRecoveryLoop, smsRecoveryEligible, onViewRecoveryConversation,
  orgId, check, onCorrected, onCorrectionFired, canWrite = true, onDirtyChange, warnedByParent = false,
}: VersantSlideoutProps) {
  const { guardedClose, setDirty, showWarning, dirty } = useUnsavedGuard(onClose)

  const plan = benefits.versant === 'found' ? benefits.plan : null
  const payerDetails = plan
    ? plan.state === 'read'
      ? { memberId: printed(plan.memberId), group: printed(plan.groupSubGroup), plan: printed(plan.planNamePrefix) }
      : { memberId: VERSANT_COULD_NOT_READ, group: VERSANT_COULD_NOT_READ, plan: VERSANT_COULD_NOT_READ }
    : undefined

  return (
    <VisionSlideoutFrame
      subtitle="Versant eligibility overview"
      ariaLabel="Versant Benefits"
      summary={summary}
      payerDetails={payerDetails}
      onClose={guardedClose}
    >
      {benefits.versant === 'not_found' ? (
        <p className="eyemed-tab-note" data-testid="versant-not-found">{VERSANT_NOT_FOUND}</p>
      ) : (
        <div className="versant-panel">
          <section className="eyemed-eligibility" aria-label="Versant patient" data-testid="versant-patient">
            <div className="eyemed-eligibility__title">Patient as Versant shows it</div>
            {benefits.plan.state === 'read' ? (
              <div className="eyemed-benefits-table">
                <div className="eyemed-benefits-row">
                  <span className="eyemed-benefits-row__label">Name</span>
                  <span className="eyemed-benefits-row__value">{printed(benefits.plan.patientName)}</span>
                </div>
                <div className="eyemed-benefits-row">
                  <span className="eyemed-benefits-row__label">Date of birth</span>
                  <span className="eyemed-benefits-row__value">{printed(benefits.plan.dateOfBirth)}</span>
                </div>
                <div className="eyemed-benefits-row">
                  <span className="eyemed-benefits-row__label">Line of Business</span>
                  <span className="eyemed-benefits-row__value">{printed(benefits.plan.lineOfBusiness)}</span>
                </div>
              </div>
            ) : (
              <p className="eyemed-eligibility__note eyemed-eligibility__note--failed">{VERSANT_COULD_NOT_READ}</p>
            )}
          </section>

          {benefits.benefitMessages.state === 'failed' ? (
            <section className="eyemed-eligibility" aria-label="Benefit Messages">
              <div className="eyemed-eligibility__title">Benefit Messages</div>
              <p className="eyemed-eligibility__note eyemed-eligibility__note--failed">{VERSANT_COULD_NOT_READ}</p>
            </section>
          ) : benefits.benefitMessages.messages.length > 0 && (
            <section className="eyemed-eligibility" aria-label="Benefit Messages" data-testid="versant-messages">
              <div className="eyemed-eligibility__title">Benefit Messages</div>
              <ol className="versant-messages">
                {benefits.benefitMessages.messages.map((message, i) => <li key={i}>{message}</li>)}
              </ol>
            </section>
          )}

          <section className="eyemed-eligibility" aria-label="Benefits" data-testid="versant-table">
            <div className="eyemed-eligibility__title">Benefits</div>
            <div className="versant-table-wrap">
              <table className="versant-table">
                <thead>
                  <tr>{benefits.benefitsTable.header.map((cell, i) => <th key={i}>{cell}</th>)}</tr>
                </thead>
                <tbody>
                  {benefits.benefitsTable.rows.map((row, r) => (
                    <tr key={r}>{row.cells.map((cell, c) => <td key={c}>{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="eyemed-eligibility" aria-label="Detailed benefits" data-testid="versant-detailed">
            <div className="eyemed-eligibility__title">Detailed benefits</div>
            {benefits.detailedBenefits.state === 'failed' ? (
              <p className="eyemed-eligibility__note eyemed-eligibility__note--failed">{VERSANT_DETAILED_FAILED}</p>
            ) : benefits.detailedBenefits.notices.length === 0 ? (
              <p className="eyemed-eligibility__note">{VERSANT_NOT_SHOWN}</p>
            ) : (
              benefits.detailedBenefits.notices.map((notice, i) => (
                <p key={i} className="versant-notice">{notice}</p>
              ))
            )}
          </section>
        </div>
      )}

      <SmsRecoverySection
        inRecoveryLoop={inRecoveryLoop}
        smsRecoveryEligible={smsRecoveryEligible}
        onView={onViewRecoveryConversation}
      />
      {orgId && check && onCorrected && canCorrect(check) && (
        <div className="failure-slideout__edit">
          <CheckIdentityEditor
            canWrite={canWrite}
            onFired={onCorrectionFired}
            orgId={orgId}
            check={check}
            onDirtyChange={d => { setDirty(d); onDirtyChange?.(d) }}
            onCorrected={onCorrected}
          />
        </div>
      )}
      {(showWarning || (warnedByParent && dirty)) && (
        <div className="slideout-unsaved" role="status">
          <span>{UNSAVED_WARNING}</span>
          <button className="slideout-unsaved__discard" onClick={onClose}>Discard</button>
        </div>
      )}
    </VisionSlideoutFrame>
  )
}
